import { useQuery } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DataTable, ColumnHelper } from "@/components/data-table"
import { api } from "@/lib/api"
import { ArrowRightLeft, RefreshCw } from "lucide-react"

const transactionColumns = [
  { key: "id", type: "text", options: { label: "ID" } },
  { key: "plan_name", type: "text", options: { label: "Plan" } },
  { key: "user_id", type: "text", options: { label: "User ID" } },
  { key: "amount", type: "number", options: { label: "Amount" } },
  { key: "status", type: "status", options: { label: "Status" } },
  { key: "order_id", type: "text", options: { label: "Order ID" } },
  { key: "created_at", type: "date", options: { label: "Date" } },
]

export default function TransactionsPage() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["pricing/purchases"],
    queryFn: () => api.get("/pricing/purchases"),
  })

  const colHelper = ColumnHelper()
  const columns = transactionColumns.map((col) => {
    if (col.type === "status") return colHelper.status(col.key, col.options)
    if (col.type === "date") return colHelper.date(col.key, col.options)
    if (col.type === "number") return colHelper.number(col.key, col.options)
    return colHelper.text(col.key, col.options)
  })

  const stats = data?.data || []
  const totalRevenue = stats.reduce((sum, t) => sum + (t.amount || 0), 0)
  const paidCount = stats.filter((t) => t.status === "paid").length

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Transactions</h1>
          <p className="text-muted-foreground">Payment transaction history</p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Transactions</CardTitle>
            <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paid</CardTitle>
            <Badge variant="success">{paidCount}</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{paidCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalRevenue.toLocaleString()} IRR</div>
          </CardContent>
        </Card>
      </div>

      <DataTable
        columns={columns}
        data={stats}
        searchKey="plan_name"
        searchPlaceholder="Search transactions..."
        onRefresh={refetch}
        isLoading={isLoading}
      />
    </div>
  )
}
